// Super Admin → Branding beheer.
// Site titel + favicon die worden gebruikt als default voor dashboard.thebrandhub.nl
// en als fallback wanneer een merk nog geen eigen logo heeft.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Header } from "./WorkspacesTab";
import { useGlobalConfig } from "@/contexts/GlobalConfigContext";

export default function BrandingTab() {
  const { refresh: refreshGlobal } = useGlobalConfig();
  const [branding, setBranding] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/super-admin/config");
        setBranding(data.branding || { site_title: "The Brand Hub", favicon_file_id: null });
        setInitial(data.branding || { site_title: "The Brand Hub", favicon_file_id: null });
      } catch { toast.error("Kon branding niet laden"); }
    })();
  }, []);

  const dirty = branding && initial && (
    (branding.site_title || "") !== (initial.site_title || "") ||
    (branding.favicon_file_id || "") !== (initial.favicon_file_id || "")
  );

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("bh_token");
      const r = await fetch(`${API}/super-admin/upload-avatar`, {
        method: "POST",
        body: fd,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Super-Admin-Token": localStorage.getItem("bh_sa_token") || "",
        },
      });
      if (!r.ok) throw new Error("Upload mislukt");
      const data = await r.json();
      setBranding((s) => ({ ...s, favicon_file_id: data.file_id }));
    } catch (e) { toast.error(e.message || "Upload mislukt"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/super-admin/config", { branding });
      toast.success("Branding opgeslagen");
      setInitial(branding);
      await refreshGlobal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  if (!branding) return <div className="text-sm text-gray-500">Laden…</div>;

  const faviconUrl = branding.favicon_file_id ? `${API}/files/${branding.favicon_file_id}` : "/favicon.svg";

  return (
    <div data-testid="sa-branding">
      <Header
        title="Branding"
        sub="Site titel + favicon voor dashboard.thebrandhub.nl. Deze worden gebruikt als default op de login- en overzichtspagina's en als fallback wanneer een merk nog geen eigen logo heeft."
      />

      <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6 mb-6 space-y-6">
        <div>
          <Label className="text-[13px]">Site titel</Label>
          <Input
            data-testid="sa-branding-title"
            value={branding.site_title || ""}
            onChange={(e) => setBranding((s) => ({ ...s, site_title: e.target.value }))}
            placeholder="The Brand Hub"
            className="mt-1 h-11 bg-white border-gray-200 max-w-md"
          />
          <p className="text-xs text-gray-500 mt-1.5">Verschijnt als browser-tab-titel. Binnen een merk wordt dit <code className="font-mono">{"{Merknaam} / {titel}"}</code>.</p>
        </div>

        <div>
          <Label className="text-[13px]">Favicon</Label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-16 h-16 rounded-[var(--global-radius)] border border-gray-200 flex items-center justify-center bg-[#f8f8f6] overflow-hidden">
              <img src={faviconUrl} alt="favicon preview" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex flex-col gap-2">
              <input ref={inputRef} type="file" hidden accept="image/png,image/svg+xml,image/jpeg,image/x-icon" onChange={(e) => upload(e.target.files?.[0])} />
              <Button
                data-testid="sa-branding-upload"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="h-9 px-4 rounded-[var(--global-radius)] bg-white border border-gray-200 text-[#1a1a1a] hover:bg-gray-50"
              >
                <UploadCloud className="w-4 h-4 mr-2" />
                {uploading ? "Uploaden…" : "Favicon uploaden"}
              </Button>
              {branding.favicon_file_id && (
                <button
                  data-testid="sa-branding-remove"
                  onClick={() => setBranding((s) => ({ ...s, favicon_file_id: null }))}
                  className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1 self-start"
                >
                  <Trash2 className="w-3 h-3" /> Terug naar default
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">PNG, SVG, JPG of ICO. Vierkant werkt het best (bijv. 512×512 of 32×32).</p>
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="sa-branding-save"
          className="h-10 px-6 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
        >{saving ? "Opslaan…" : "Opslaan"}</Button>
      </div>
    </div>
  );
}
